import { readWord, splitSyllables } from "@kensio/pinyinjs";

/**
 * Cutting typed pinyin into syllables, so each can be painted its own tone.
 *
 * What `./fuxi-pinyin.ts` draws over the field. Split from it because that
 * module is about a caret and this one is about the language, and because a
 * cut that gets a syllable wrong is worth a case of its own.
 *
 * **Every character the reader typed comes back, in order.** The pieces
 * concatenate to exactly what went in, `splitSyllables` being asked for the
 * spellings rather than `writeSyllable` for tidied ones. The field and the ink
 * over it have to agree character for character or the two drift apart the
 * first time a reader types something the dictionary cannot read.
 *
 * **A run the dictionary cannot read is one uncoloured piece.** `xq9` is
 * somebody's typing and half of `bing` is a reader mid-word, and neither is
 * something to guess a tone for.
 */

/** One piece of what was typed, and the tone to paint it. */
export interface TonedPiece {
  /** The characters, exactly as the reader typed them. */
  readonly text: string;

  /** Its tone, or nothing where this piece is not a syllable. */
  readonly tone: number | undefined;
}

/**
 * A run of the letters pinyin is written with and the digits it is typed with.
 *
 * Latin covers the letters and the marks over them alike, so a syllable
 * already carrying its mark stays in the same run as the one being typed
 * behind it. `v` and a colon are both ways of typing ü on a keyboard that has
 * no ü. Everything else ends the run: a space, and the 隔音符号 above all,
 * which is a boundary the reader wrote themselves and one this must not eat.
 */
export const pinyinRun = /[\p{Script=Latin}:1-5]+/gu;

/** One run, cut into syllables, or left whole where it is not pinyin. */
function cut(written: string): readonly TonedPiece[] {
  const spellings = splitSyllables(written);
  const read = readWord(written);

  return spellings === undefined ||
    read === undefined ||
    spellings.length !== read.length
    ? [{ text: written, tone: undefined }]
    : spellings.map((text, at) => ({ text, tone: read[at]?.tone }));
}

/**
 * What the reader has typed, cut into syllables and whatever is between them.
 *
 * @param text - The field's value, as it stands.
 * @returns The pieces, in order, concatenating to what went in.
 */
export function tonedPieces(text: string): readonly TonedPiece[] {
  const pieces: TonedPiece[] = [];
  let at = 0;

  for (const found of text.matchAll(pinyinRun)) {
    pieces.push({ text: text.slice(at, found.index), tone: undefined });
    pieces.push(...cut(found[0]));
    at = found.index + found[0].length;
  }

  pieces.push({ text: text.slice(at), tone: undefined });

  return pieces.filter((piece) => piece.text !== "");
}
